import { useSyncExternalStore } from "react";

import { parseCompanySituation, type MorrowCompanySituation } from "./morrowCompanyBrief";
import type { OrgClerkRole } from "./orgMembers";

/**
 * First-run Morrow workspace onboarding. Company profile is stored locally so
 * the chat landing can greet by name after the wizard; completion is what
 * hides the wizard on later visits.
 */
export const MORROW_ONBOARDING_STORAGE_KEY = "morrow:onboarding:v2";

export type MorrowAccountType = "individual" | "company";

/**
 * A teammate captured on the onboarding invite step. `role` stays for backward
 * compatibility with earlier persisted profiles and the local Team-page pending
 * display; the richer fields mirror the Team page's add-member form so an invited
 * teammate arrives fully described:
 *   - `functionRole` — "what they do" (maps to `function_role` in `org_members`)
 *   - `description`  — an "about" blurb
 *   - `clerkRole`    — the Clerk access level to invite them at (admin/member)
 * All of the rich fields are optional so an email-only invite still works.
 */
export type MorrowInvitedPerson = {
  readonly email: string;
  readonly title: string;
  readonly role: string;
  readonly functionRole?: string;
  readonly description?: string;
  readonly clerkRole?: OrgClerkRole;
};

/**
 * The short questionnaire captured after account type. Every field is optional
 * so the step can be partially filled, but `role` is the signal we treat as
 * "the questionnaire has been answered" (see MorrowOnboarding.logic). Answers
 * also drive the connect step's recommended-toolkit set.
 */
export type MorrowQuestionnaire = {
  readonly role?: string;
  readonly industry?: string;
  readonly teamSize?: string;
  readonly goal?: string;
};

export type MorrowOnboardingProfile = {
  readonly accountType: MorrowAccountType | null;
  readonly companyName: string;
  readonly companyWebsite: string;
  readonly companyLogoUrl: string;
  readonly companyBrief: string;
  readonly companySituation: MorrowCompanySituation | "";
  readonly questionnaire: MorrowQuestionnaire;
  readonly invitedPeople: ReadonlyArray<MorrowInvitedPerson>;
  readonly completed: boolean;
};

const EMPTY_PROFILE: MorrowOnboardingProfile = {
  accountType: null,
  companyName: "",
  companyWebsite: "",
  companyLogoUrl: "",
  companyBrief: "",
  companySituation: "",
  questionnaire: {},
  invitedPeople: [],
  completed: false,
};

const listeners = new Set<() => void>();
let cachedProfile: MorrowOnboardingProfile | null = null;

function safeStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseInvitedPerson(value: unknown): MorrowInvitedPerson | null {
  if (typeof value === "string") {
    const email = value.trim().toLowerCase();
    return email ? { email, title: "", role: "" } : null;
  }
  if (!isRecord(value)) return null;
  const email = typeof value.email === "string" ? value.email.trim().toLowerCase() : "";
  if (!email) return null;
  const functionRole = typeof value.functionRole === "string" ? value.functionRole.trim() : "";
  const description = typeof value.description === "string" ? value.description.trim() : "";
  const clerkRole = parseClerkRole(value.clerkRole);
  // Only carry the optional rich fields when they hold a value so
  // `exactOptionalPropertyTypes` stays happy and email-only invites round-trip
  // without empty keys.
  return {
    email,
    title: typeof value.title === "string" ? value.title.trim() : "",
    role: typeof value.role === "string" ? value.role.trim() : "",
    ...(functionRole ? { functionRole } : {}),
    ...(description ? { description } : {}),
    ...(clerkRole ? { clerkRole } : {}),
  };
}

/** Narrow a persisted value to a Clerk org role, else undefined. */
function parseClerkRole(value: unknown): OrgClerkRole | undefined {
  return value === "org:admin" || value === "org:member" ? value : undefined;
}

function parseInvitedPeople(value: Record<string, unknown>): MorrowInvitedPerson[] {
  if (Array.isArray(value.invitedPeople)) {
    return value.invitedPeople.flatMap((item) => {
      const parsed = parseInvitedPerson(item);
      return parsed ? [parsed] : [];
    });
  }
  if (Array.isArray(value.invitedEmails)) {
    return value.invitedEmails.flatMap((item) => {
      const parsed = parseInvitedPerson(item);
      return parsed ? [parsed] : [];
    });
  }
  return [];
}

export function invitedPeopleFromUnknown(value: unknown): MorrowInvitedPerson[] {
  if (!isRecord(value)) return [];
  return parseInvitedPeople(value);
}

/** Pull a trimmed string field off a persisted questionnaire blob, else undefined. */
function parseQuestionnaireField(value: Record<string, unknown>, key: string): string | undefined {
  const raw = value[key];
  if (typeof raw !== "string") return undefined;
  const trimmed = raw.trim();
  return trimmed ? trimmed : undefined;
}

function parseQuestionnaire(value: unknown): MorrowQuestionnaire {
  if (!isRecord(value)) return {};
  const questionnaire: MorrowQuestionnaire = {};
  const role = parseQuestionnaireField(value, "role");
  const industry = parseQuestionnaireField(value, "industry");
  const teamSize = parseQuestionnaireField(value, "teamSize");
  const goal = parseQuestionnaireField(value, "goal");
  // Keep only the fields that were actually answered so `exactOptionalProperty`
  // stays happy and `questionnaire.role` is a reliable "answered?" signal.
  return {
    ...questionnaire,
    ...(role ? { role } : {}),
    ...(industry ? { industry } : {}),
    ...(teamSize ? { teamSize } : {}),
    ...(goal ? { goal } : {}),
  };
}

function parseProfile(value: unknown): MorrowOnboardingProfile | null {
  if (!isRecord(value)) return null;
  const accountType =
    value.accountType === "individual" || value.accountType === "company"
      ? value.accountType
      : null;
  const companyName = typeof value.companyName === "string" ? value.companyName.trim() : "";
  const companyWebsite =
    typeof value.companyWebsite === "string" ? value.companyWebsite.trim() : "";
  const companyBrief = typeof value.companyBrief === "string" ? value.companyBrief.trim() : "";
  return {
    accountType,
    companyName,
    companyWebsite,
    companyLogoUrl: parseCompanyLogoUrl(value.companyLogoUrl),
    companyBrief,
    companySituation: parseCompanySituation(value.companySituation),
    questionnaire: parseQuestionnaire(value.questionnaire),
    invitedPeople: parseInvitedPeople(value),
    completed: value.completed === true,
  };
}

export function parseCompanyLogoUrl(value: unknown): string {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("data:image/")) return trimmed;
  try {
    const url = new URL(trimmed);
    return url.protocol === "http:" || url.protocol === "https:" ? trimmed : "";
  } catch {
    return "";
  }
}

export function readCompanyLogoFile(file: File): Promise<string | null> {
  if (!file.type.startsWith("image/")) return Promise.resolve(null);
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      resolve(result.startsWith("data:image/") ? result : null);
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

export async function companyLogoFileFromUrl(url: string): Promise<File | null> {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    if (!blob.type.startsWith("image/")) return null;
    const ext = blob.type.split("/")[1]?.split("+")[0] || "png";
    return new File([blob], `logo.${ext}`, { type: blob.type });
  } catch {
    return null;
  }
}

function readProfile(): MorrowOnboardingProfile {
  const storage = safeStorage();
  if (!storage) return EMPTY_PROFILE;
  try {
    const raw = storage.getItem(MORROW_ONBOARDING_STORAGE_KEY);
    if (!raw) return EMPTY_PROFILE;
    return parseProfile(JSON.parse(raw)) ?? EMPTY_PROFILE;
  } catch {
    return EMPTY_PROFILE;
  }
}

function emit() {
  for (const listener of listeners) listener();
}

export function getOnboardingProfile(): MorrowOnboardingProfile {
  return (cachedProfile ??= readProfile());
}

export function subscribeToOnboarding(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function resetOnboardingProfile(): void {
  const storage = safeStorage();
  if (storage) {
    try {
      storage.removeItem(MORROW_ONBOARDING_STORAGE_KEY);
    } catch {
      // Best-effort.
    }
  }
  cachedProfile = null;
  emit();
}

export function writeOnboardingProfile(
  patch: Partial<MorrowOnboardingProfile>,
): MorrowOnboardingProfile {
  const current = getOnboardingProfile();
  const next: MorrowOnboardingProfile = {
    ...current,
    ...patch,
    companyLogoUrl:
      patch.companyLogoUrl !== undefined
        ? parseCompanyLogoUrl(patch.companyLogoUrl)
        : current.companyLogoUrl,
    // Questionnaire is merged field-by-field so a patch that only sets, say, the
    // goal never wipes a role captured on an earlier step.
    questionnaire: patch.questionnaire
      ? { ...current.questionnaire, ...patch.questionnaire }
      : current.questionnaire,
    invitedPeople: patch.invitedPeople ? [...patch.invitedPeople] : current.invitedPeople,
  };
  const storage = safeStorage();
  if (storage) {
    try {
      storage.setItem(MORROW_ONBOARDING_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Best-effort; the in-memory snapshot still updates for this session.
    }
  }
  cachedProfile = next;
  emit();
  return next;
}

export function useOnboardingProfile(): MorrowOnboardingProfile {
  return useSyncExternalStore(subscribeToOnboarding, getOnboardingProfile, getOnboardingProfile);
}

export function completeOnboarding(
  patch?: Partial<MorrowOnboardingProfile>,
): MorrowOnboardingProfile {
  return writeOnboardingProfile({ ...patch, completed: true });
}

export function addInvitedPerson(email: string): MorrowInvitedPerson | null {
  const normalized = email.trim().toLowerCase();
  if (!isInviteEmail(normalized)) return null;
  const current = getOnboardingProfile();
  const existing = current.invitedPeople.find((person) => person.email === normalized);
  if (existing) return existing;
  const person: MorrowInvitedPerson = { email: normalized, title: "", role: "" };
  writeOnboardingProfile({
    invitedPeople: [...current.invitedPeople, person],
  });
  return person;
}

/** Undo an optimistic invite (e.g. when the server/Clerk rejects it — out of seats). */
export function removeInvitedPerson(email: string): void {
  const normalized = email.trim().toLowerCase();
  const current = getOnboardingProfile();
  const next = current.invitedPeople.filter((person) => person.email !== normalized);
  if (next.length !== current.invitedPeople.length) {
    writeOnboardingProfile({ invitedPeople: next });
  }
}

/** Accepts bare domains, http(s) URLs, and LinkedIn pages. Empty input stays empty. */
export function normalizeCompanyWebsite(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  try {
    const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const url = new URL(withProtocol);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    if (!url.hostname.includes(".")) return null;
    return url.toString();
  } catch {
    return null;
  }
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isInviteEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim());
}
